import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import Faq from "@/components/Faq";
import SyringeGuide from "@/components/SyringeGuide";
import UnitConverter from "@/components/UnitConverter";
import BmiCalculator from "@/components/BmiCalculator";
import { Reveal, Label } from "@/components/ui-bits";
import { TOOLS } from "@/data/tools";
import NotFound from "./NotFound";

const PAGES = {
  "syringe-guide": {
    label: "Tools — injectable patients",
    title: "TRT syringe fill guide",
    lede: "Choose your medication strength, enter the dose you have been prescribed, and see exactly where to fill a 0.5, 1 or 3 mL syringe — with dead space accounted for.",
    seo: "TRT syringe fill guide: calculate the fill line for your prescribed testosterone dose (mg or mL) across common UK concentrations, including syringe dead space. Educational tool, not injection instruction.",
    Component: SyringeGuide,
    sections: [
      {
        h2: "Why dead space matters",
        p: "A small amount of liquid stays in the needle hub and syringe tip after the plunger is fully pressed — typically around 0.05 mL. For a 0.3 mL dose at 250 mg/mL that is roughly 12 mg of testosterone, which is why consistent syringe choice and measurement help keep levels stable between injections.",
      },
      {
        h2: "Why small differences matter",
        p: "Testosterone is supplied in concentrated solutions, so a 0.05 mL slip equates to 10–12.5 mg. Repeated inconsistencies can contribute to fluctuating levels and more variable symptoms. Use the same syringe type each time and measure at eye level.",
      },
      {
        h2: "What this tool is not",
        p: "It does not teach injection technique, choose a dose, or replace the written instructions from your prescribing clinician or pharmacist. Dosing decisions are made from your blood results and symptoms, not from a syringe.",
      },
    ],
    faqs: [
      { q: "Can I use this for hCG or other medicines?", a: "No. hCG is reconstituted and measured very differently. This guide is for testosterone injections only." },
      { q: "Which syringe should I use for small volumes?", a: "A 0.5 or 1 mL syringe reads small volumes far more accurately than a 3 mL syringe. Your clinic will specify which to use." },
      { q: "Does the guide tell me how to inject?", a: "No. Technique training is part of starting injectable treatment and is delivered by your clinical team." },
    ],
  },
  "unit-converter": {
    label: "Tools — reading results",
    title: "Testosterone unit converter",
    lede: "UK laboratories report in nmol/L; US and many online sources use ng/dL. Convert total and free testosterone, oestradiol, SHBG, prolactin and haemoglobin between units and read them in UK context.",
    seo: "Convert testosterone blood results between nmol/L and ng/dL, free testosterone pmol/L to pg/mL, oestradiol, SHBG, prolactin and haemoglobin — with UK reference context.",
    Component: UnitConverter,
    sections: [
      {
        h2: "Why the units confuse people",
        p: "A result of '500' from an American laboratory sounds very different from a UK result of '17', yet they describe almost the same level: 500 ng/dL ÷ 28.84 ≈ 17.3 nmol/L. Forum advice and research papers routinely mix the two, which is how men come to believe their normal level is dangerously low — or the reverse.",
      },
      {
        h2: "Converting is not interpreting",
        p: "Reference ranges differ between assays, samples taken in the afternoon read lower, and a total testosterone within range can still be functionally low when SHBG is high. Use the converter to compare numbers, then let a clinician interpret them alongside your symptoms.",
      },
    ],
    faqs: [
      { q: "Is 12 nmol/L low?", a: "It is within most UK reference ranges but near the lower end. Whether it is 'low for you' depends on symptoms, SHBG and free testosterone, timing, and repeat testing." },
      { q: "Why does my free testosterone look so small?", a: "Only around 2–3% of testosterone is free, so it is reported in picomoles (pmol/L) rather than nanomoles. 250 pmol/L is 0.25 nmol/L." },
    ],
  },
  "bmi-calculator": {
    label: "Tools — metabolic context",
    title: "BMI & waist-to-height calculator",
    lede: "Body fat is the single biggest modifiable driver of low testosterone. Check your BMI and waist-to-height ratio against the NHS categories — and understand what the numbers mean for your hormones.",
    seo: "BMI and waist-to-height calculator with NHS categories, metric and imperial units, and plain-English context on how body fat affects testosterone.",
    Component: BmiCalculator,
    sections: [
      {
        h2: "Body fat and testosterone",
        p: "Fat tissue contains aromatase, the enzyme that converts testosterone to oestrogen. More fat means more conversion, a weaker pituitary signal and lower SHBG — a combination that lowers total testosterone and can produce the symptoms men attribute to ageing. Meaningful weight loss reverses much of this.",
      },
      {
        h2: "Why we show waist-to-height too",
        p: "BMI cannot tell muscle from fat and misclassifies some athletic men. Waist-to-height ratio tracks the abdominal fat that matters most metabolically; keeping your waist under half your height is the simple rule of thumb used in UK guidance.",
      },
    ],
    faqs: [
      { q: "Will losing weight fix my testosterone?", a: "For men whose levels are suppressed by excess weight, often substantially — around 10% body weight loss produces meaningful rises. For men with primary testicular or pituitary causes, weight loss helps health but will not restore levels on its own." },
      { q: "Does Androgen Health refuse treatment if my BMI is high?", a: "No. We assess the whole picture. Where weight is a plausible contributor we will say so and may suggest addressing it first or alongside treatment, with a retest." },
    ],
  },
};

export function ToolsHub() {
  return (
    <>
      <Seo title="TRT Tools & Calculators | Androgen Health" description="Free, honest tools for testosterone health: ADAM symptom screen, unit converter, syringe fill guide and BMI calculator — each explained in UK clinical context." />
      <PageHero label="Tools" title={<>Useful tools.<br /><span className="serif-it text-teal">Honest context.</span></>} lede="Calculators and guides that help you understand your results and treatment — each one clear about what it can and cannot tell you." safety={false} />
      <section className="pb-24 px-5 sm:px-8" data-testid="tools-hub">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 gap-5">
          {TOOLS.map((t, i) => (
            <Reveal key={t.slug} delay={i * 0.06} className="h-full">
              <Link to={t.to} className="card-line rounded-2xl p-8 flex flex-col h-full group hover:border-teal/30 transition-colors" data-testid={`tool-card-${t.slug}`}>
                <p className="mono-label text-dusk">{t.kicker}</p>
                <h2 className="font-display text-3xl tracking-wide mt-4 group-hover:text-teal transition-colors">{t.name}</h2>
                <p className="text-mute text-sm leading-relaxed mt-3 flex-1">{t.copy}</p>
                <span className="inline-flex items-center gap-2 text-teal text-sm mt-6 group-hover:gap-3 transition-all">Open tool <ArrowRight size={16} /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}

export default function ToolPage({ slug }) {
  const page = PAGES[slug];
  if (!page) return <NotFound />;
  const { Component } = page;
  return (
    <>
      <Seo title={`${page.title} | Androgen Health`} description={page.seo} />
      <PageHero label={page.label} title={page.title} lede={page.lede} safety={false} />
      <section className="pb-20 px-5 sm:px-8" data-testid={`tool-page-${slug}`}>
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <Component />
          </Reveal>
          <div className="mt-20 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 space-y-10">
              {page.sections.map((s) => (
                <Reveal key={s.h2}>
                  <h2 className="font-display font-semibold text-3xl tracking-wide">{s.h2}</h2>
                  <p className="text-mute leading-relaxed mt-4">{s.p}</p>
                </Reveal>
              ))}
            </div>
            <div className="lg:col-span-5">
              <Reveal>
                <Label>Common questions</Label>
                <div className="mt-5"><Faq items={page.faqs} /></div>
              </Reveal>
              <Reveal>
                <div className="mt-10 flex flex-wrap gap-3">
                  {TOOLS.filter((t) => t.slug !== slug).map((t) => (
                    <Link key={t.slug} to={t.to} className="px-4 py-2 rounded-full border border-line text-sm text-mute hover:text-teal hover:border-teal/40 transition-colors" data-testid={`tool-related-${t.slug}`}>{t.name}</Link>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
          <p className="text-xs text-dusk leading-relaxed mt-16 max-w-3xl">Educational tool only. It does not provide diagnosis, dosing decisions or injection instruction, and does not replace advice from your prescribing clinician or pharmacist. Treatment is subject to individual clinical assessment.</p>
        </div>
      </section>
    </>
  );
}
